// Press & media page.
//
// Journalists, YouTubers and TCG newsletter writers need three things before
// they will cite a site: what it is, what data they may quote, and how to reach
// a human. Scattering those across /about, /tcg-price-index and /contact cost us
// citations, so this page collects them in one linkable place.
//
// No publish/update dates are rendered or emitted in schema (site-wide rule).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPriceBoard } from "@/lib/prices.functions";
import { priceBoardQueryOptions } from "@/lib/price-board-query";
import { buildPriceRows } from "@/lib/prices-shared";
import { buildIndexBoard, formatChange, formatLevel } from "@/lib/price-index.shared";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { breadcrumbSchema } from "@/lib/schema";
import { PALS } from "@/lib/pals-data";
import { OFFICIAL_CARDS } from "@/lib/tcg-cards";

const URL_ = "https://palstars.com/press";
const TITLE = "Press & Media Kit — Pal Stars";
const DESCRIPTION =
  "Press kit for Pal Stars: what the Palworld TCG Price Index measures, which data journalists and creators may quote, citation wording, and media contact details.";

const FAQ: FaqItem[] = [
  {
    q: "Can I quote Pal Stars data in an article or video?",
    a: "Yes. Pal Stars data — the Palworld TCG Price Index level, card values, breadth figures and pull-rate tables — may be quoted free of charge as long as you attribute Pal Stars and link to palstars.com. No prior permission is needed.",
  },
  {
    q: "Is there a machine-readable Palworld card price feed?",
    a: "Yes. The public JSON feed at palstars.com/api/public/price-index.json returns the current index level, the daily history and the sub-index levels, so you can build your own chart or on-screen graphic without scraping the page.",
  },
  {
    q: "Who runs Pal Stars and is it official?",
    a: "Pal Stars is an independent, unofficial Palworld fan wiki and card market tracker. It is not affiliated with, endorsed by or sponsored by Pocketpair, Inc. or Bushiroad. Palworld and the Palworld OFFICIAL CARD GAME are the property of their respective owners.",
  },
  {
    q: "How do I contact Pal Stars for press enquiries?",
    a: "Email info@palstars.com or use the contact form at palstars.com/contact. Please include your outlet, deadline and the data point you want to check so we can answer with a source.",
  },
  {
    q: "How current is the Palworld card price data?",
    a: "The marketplace sync runs once every 24 hours at 07:10 UTC, and every price surface on the site reads that same board, so any figure you quote from Pal Stars is from the most recent completed sync.",
  },
];

export const Route = createFileRoute("/press")({
  // Server-render the index board so crawlers see the quotable figures.
  loader: ({ context }) => context.queryClient.ensureQueryData(priceBoardQueryOptions()),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Pal Stars press & media kit" },
      {
        property: "og:description",
        content:
          "Quotable Palworld TCG market data, citation wording, public JSON feed and media contact for Pal Stars.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL_ },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Pal Stars press & media kit" },
      {
        name: "twitter:description",
        content: "Quotable Palworld TCG market data, citation wording and media contact for Pal Stars.",
      },
    ],
    links: [{ rel: "canonical", href: URL_ }],
    scripts: [
      faqSchema(FAQ, URL_),
      breadcrumbSchema([
        ["Home", "/"],
        ["Press & Media", "/press"],
      ]),
    ],
  }),
  component: PressPage,
});

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <p className="text-xs text-white/50">{label}</p>
      <p className="mt-1 text-xl font-extrabold tabular-nums">{value}</p>
    </div>
  );
}

function PressPage() {
  const { data } = useQuery({ queryKey: ["price-board"], queryFn: () => getPriceBoard(), staleTime: 10 * 60 * 1000 });
  const board = useMemo(() => buildIndexBoard(data?.rows ?? buildPriceRows([])), [data]);
  const c = board.composite;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Pal Stars Press &amp; Media Kit</h1>
      <p className="aeo-answer mt-3 text-white/80">
        Pal Stars is an independent Palworld wiki and Palworld OFFICIAL CARD GAME market tracker. Journalists, streamers
        and newsletter writers may quote any figure on this site — the Palworld TCG Price Index, individual card values,
        breadth data and pull-rate tables — free of charge, with attribution and a link to palstars.com.
      </p>

      <section className="mt-8">
        <h2 className="text-xl font-bold">Site at a glance</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <Card label="Pals documented" value={String(PALS.length)} />
          <Card label="Card printings tracked" value={String(OFFICIAL_CARDS.length)} />
          <Card label="Index level" value={c ? formatLevel(c.level) : "—"} />
          <Card label="Index 7-day move" value={c ? formatChange(c.change7d) : "—"} />
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-5">
        <h2 className="text-xl font-bold">What you can quote</h2>
        <ul className="mt-3 space-y-2 text-sm text-white/75">
          <li>
            <strong className="text-white">Palworld TCG Price Index</strong> — an equal-weight, base-1,000 benchmark of
            every tracked Palworld card printing. Methodology is published on the{" "}
            <Link to="/tcg-price-index" className="text-primary underline">
              index page
            </Link>
            .
          </li>
          <li>
            <strong className="text-white">Weekly movers</strong> — risers, fallers and market breadth in the{" "}
            <Link to="/tcg-market-report" className="text-primary underline">
              Palworld TCG market report
            </Link>
            .
          </li>
          <li>
            <strong className="text-white">Per-card values and history</strong> — validated marketplace pricing on the{" "}
            <Link to="/prices" className="text-primary underline">
              Palworld card price chart
            </Link>
            .
          </li>
          <li>
            <strong className="text-white">Pull rates and pack maths</strong> — the{" "}
            <Link to="/pack-calculator" className="text-primary underline">
              pack calculator
            </Link>{" "}
            and{" "}
            <Link to="/booster-pack" className="text-primary underline">
              booster pack simulator
            </Link>
            .
          </li>
        </ul>
      </section>

      <section className="mt-8 rounded-3xl border border-emerald-400/25 bg-emerald-400/5 p-5">
        <h2 className="text-xl font-bold">Citation wording</h2>
        <p className="mt-2 rounded-lg bg-black/30 p-3 font-mono text-xs text-white/85">
          Pal Stars TCG Price Index: {c ? formatLevel(c.level) : "1,000"} (equal-weight, base 1,000
          {c ? `, ${c.constituents} tracked printings` : ""}) — https://palstars.com/tcg-price-index
        </p>
        <p className="mt-3 text-sm text-white/70">
          Machine-readable feed:{" "}
          <a className="text-primary underline" href="/api/public/price-index.json" rel="nofollow">
            /api/public/price-index.json
          </a>
          . Please credit “Pal Stars” and link the page you took the figure from.
        </p>
      </section>

      <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-5">
        <h2 className="text-xl font-bold">Brand and imagery</h2>
        <p className="mt-2 text-sm text-white/75">
          You may use the Pal Stars name and logo when reporting on the site. Card images on Pal Stars are official
          publisher scans reproduced for identification and commentary — please source card art from the publisher
          directly rather than from Pal Stars. Palworld is © Pocketpair, Inc.; the Palworld OFFICIAL CARD GAME is
          published by Bushiroad. Pal Stars is unofficial and unaffiliated.
        </p>
      </section>

      <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-5">
        <h2 className="text-xl font-bold">Media contact</h2>
        <p className="mt-2 text-sm text-white/75">
          Email <span className="font-semibold text-white">info@palstars.com</span> or use the{" "}
          <Link to="/contact" className="text-primary underline">
            contact form
          </Link>
          . Include your outlet, deadline and the exact figure you want verified and we will reply with the source and
          the sync timestamp behind it. Background on the project lives on the{" "}
          <Link to="/about" className="text-primary underline">
            about page
          </Link>
          .
        </p>
      </section>

      <FaqSection items={FAQ} heading="Press FAQ" />
      <InternalLinkBlock path="/tcg-price-index" />
    </div>
  );
}
